import { UserType } from "@elation/api-client/src/stitch-web/models/userType";
import { authClient } from "@src/auth/client";

interface OktaConfig {
  clientId: string;
  issuer: string;
  scopes: string[];
}

/**
 * Get the Chrome extension redirect URI for OAuth using Chrome's identity API.
 */
export function getChromeExtensionRedirectUri(): string {
  return chrome.identity.getRedirectURL();
}

/**
 * Generate a random state parameter for OAuth CSRF protection.
 */
function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

/**
 * Generate a nonce for implicit flow, required when requesting id_token.
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

/**
 * Build the Okta authorization URL for implicit flow.
 */
function buildImplicitAuthorizationUrl(
  config: OktaConfig,
  state: string,
  nonce: string,
): string {
  const redirectUri = getChromeExtensionRedirectUri();

  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: "token id_token",
    scope: config.scopes.join(" "),
    redirect_uri: redirectUri,
    state: state,
    nonce: nonce,
    response_mode: "fragment",
  });

  const issuerUrl = config.issuer.endsWith("/")
    ? config.issuer.slice(0, -1)
    : config.issuer;

  return `${issuerUrl}/v1/authorize?${params.toString()}`;
}

/**
 * Parse the redirect URL fragment to extract tokens from implicit flow response.
 */
function parseImplicitRedirectUrl(url: string): {
  access_token?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: string;
  scope?: string;
  state?: string;
  error?: string;
  error_description?: string;
} {
  const urlObj = new URL(url);
  const fragment = urlObj.hash.substring(1);
  const params = new URLSearchParams(fragment);

  return {
    access_token: params.get("access_token") || undefined,
    id_token: params.get("id_token") || undefined,
    token_type: params.get("token_type") || undefined,
    expires_in: params.get("expires_in") || undefined,
    scope: params.get("scope") || undefined,
    state: params.get("state") || undefined,
    error: params.get("error") || undefined,
    error_description: params.get("error_description") || undefined,
  };
}

/**
 * Initiate OAuth login flow for Chrome extension using launchWebAuthFlow.
 * Uses Implicit Flow since Okta doesn't allow chrome-extension:// in Trusted Origins.
 */
export async function chromeExtensionOktaLogin(
  config: OktaConfig,
  type: UserType,
): Promise<{ success: boolean; error?: string }> {
  const state = generateState();
  const nonce = generateNonce();

  try {
    const authUrl = buildImplicitAuthorizationUrl(config, state, nonce);

    console.log("[Chrome OAuth] Starting launchWebAuthFlow");
    console.log("[Chrome OAuth] Authorization URL:", authUrl);
    console.log(
      "[Chrome OAuth] Redirect URI:",
      getChromeExtensionRedirectUri(),
    );

    const responseUrl = await new Promise<string>((resolve, reject) => {
      chrome.identity.launchWebAuthFlow(
        {
          url: authUrl,
          interactive: true,
        },
        (responseUrl) => {
          const lastError =
            "lastError" in chrome.runtime
              ? (chrome.runtime as unknown as { lastError: Error }).lastError
              : undefined;

          if (lastError) {
            reject(new Error(lastError.message || "Unknown Chrome error"));
            return;
          }
          if (!responseUrl) {
            reject(new Error("No response URL received"));
            return;
          }
          resolve(responseUrl);
        },
      );
    });

    const tokenResponse = parseImplicitRedirectUrl(responseUrl);

    if (tokenResponse.error) {
      throw new Error(tokenResponse.error_description || tokenResponse.error);
    }

    if (!tokenResponse.access_token) {
      throw new Error("No access token received");
    }

    if (!tokenResponse.id_token) {
      throw new Error("No id token received");
    }

    if (tokenResponse.state !== state) {
      throw new Error("State mismatch");
    }

    await authClient.$fetch("/exchange/chrome/okta", {
      method: "POST",
      body: {
        idToken: tokenResponse.id_token,
        accessToken: tokenResponse.access_token,
        type: type,
      },
    });

    console.log("[Chrome OAuth] Login successful");

    return { success: true };
  } catch (error) {
    console.error("[Chrome OAuth] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
